import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTranslation } from 'react-i18next';
import { uploadCostSheets } from '../services/api';

const CostSheetUpload = ({ projectId, onUploadSuccess }) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    if (!file.name.endsWith('.xlsx')) {
      setError(t('costSheetUpload.pleaseUploadExcel'));
      setUploadResult(null);
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const response = await uploadCostSheets(projectId, file);
      setUploadResult(response.data);
      if (typeof onUploadSuccess === 'function') {
        onUploadSuccess(response.data);
      }
    } catch (err) {
      const message = err.response?.data?.error || err.message || t('costSheetUpload.uploadFailed');
      setError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'grey.300',
        borderRadius: 2,
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
      }}
    >
      <input {...getInputProps()} />
      {isUploading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography>{t('costSheetUpload.uploading')}</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h6">
            {isDragActive ? t('costSheetUpload.dropHere') : t('costSheetUpload.dragAndDrop')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('costSheetUpload.onlyXlsx')}
          </Typography>
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
          {uploadResult && (
            <Box sx={{ width: '100%', mt: 1 }}>
              <Alert
                severity={uploadResult.costSheetsCreated > 0 ? 'success' : 'warning'}
                sx={{ textAlign: 'left' }}
              >
                {t('costSheetUpload.createdResult', {
                  costSheets: uploadResult.costSheetsCreated || 0,
                  categories: uploadResult.categoriesCreated || 0,
                })}
                {(uploadResult.errors || []).length > 0 && (
                  <>
                    {' '}{t('costSheetUpload.importWarnings')} {(uploadResult.errors || []).slice(0, 3).join(' | ')}
                    {(uploadResult.errors || []).length > 3 ? ' | ...' : ''}
                  </>
                )}
              </Alert>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CostSheetUpload;
